import { PageType } from '@app-types/PageType'
import { useUserData } from '@contexts/UserDataContext'
import dayjs from 'dayjs'
import { SessionStorageHelper } from 'helpers/sessionStorageHelper'
import { calculateAndGetNewStats } from 'helpers/stats'
import { getEstReadTime } from 'helpers/userData'
import { useEffect, useRef, useState } from 'react'

type UserHistoryType = {
  type: PageType
  page: number
  time: number
}

const DEFAULT_MINIMUM_STATS_ENTRY_TIME_MS = 7000
const REQUIRED_ESTIMATED_READ_TIME_RATIO = 2 / 3
const COMPLETION_VISIBILITY_OFFSET_PX = 24
const READING_PAGE_END_SELECTOR = '[data-reading-page-end="true"]'

/** Returns the time required at the page end before recording reading progress. */
const getMinimumStatsEntryTime = (estimatedReadTimeMs: number) =>
  estimatedReadTimeMs > 0
    ? estimatedReadTimeMs * REQUIRED_ESTIMATED_READ_TIME_RATIO
    : DEFAULT_MINIMUM_STATS_ENTRY_TIME_MS

const hasReachedDocumentEnd = () => {
  const documentElement = document.documentElement
  const scrollHeight = Math.max(documentElement.scrollHeight, document.body.scrollHeight)
  const clientHeight = documentElement.clientHeight
  const scrollableDistance = scrollHeight - clientHeight

  if (scrollableDistance <= COMPLETION_VISIBILITY_OFFSET_PX) {
    return false
  }

  const scrollTop = window.scrollY || documentElement.scrollTop || document.body.scrollTop
  return scrollTop + window.innerHeight >= scrollHeight - COMPLETION_VISIBILITY_OFFSET_PX
}

const hasReachedReadingPageEnd = () => {
  const readingPageEnd = document.querySelector(READING_PAGE_END_SELECTOR)

  if (!readingPageEnd) {
    return hasReachedDocumentEnd()
  }

  const { top } = readingPageEnd.getBoundingClientRect()
  return top <= window.innerHeight + COMPLETION_VISIBILITY_OFFSET_PX
}

export const useReadingProgress = (
  type: PageType,
  page: number,
  notify: (_msg?: string) => void,
) => {
  const pageViewTimeRef = useRef<number>(Date.now())
  const statsRecordedRef = useRef(false)
  const [, setUserHistory] = useState<UserHistoryType[]>([])
  const { userData, updateUserData, loading } = useUserData()

  const latestDataRef = useRef({ userData, updateUserData, notify })

  useEffect(() => {
    latestDataRef.current = { userData, updateUserData, notify }
  }, [userData, updateUserData, notify])

  useEffect(() => {
    if (loading) return

    pageViewTimeRef.current = Date.now()
    statsRecordedRef.current = false
    const estimatedReadTimeMs = getEstReadTime(type) * 1000
    const minimumStatsEntryTimeMs = getMinimumStatsEntryTime(estimatedReadTimeMs)

    const addCurrentPageToStats = () => {
      if (statsRecordedRef.current) {
        return
      }

      const { userData, updateUserData, notify } = latestDataRef.current
      const counterStatus = SessionStorageHelper.get('stopWatchStatus')

      if (counterStatus === 'false') {
        return
      }

      const now = dayjs()
      const statsDate =
        userData.delayedStatsEntry && now.hour() < 3
          ? now.subtract(1, 'day').format('DD-MM-YYYY')
          : now.format('DD-MM-YYYY')

      const newStats = calculateAndGetNewStats({
        type,
        readPageCount: 1,
        userData,
        date: statsDate,
      })

      if (newStats && Object.keys(newStats).length > 0) {
        statsRecordedRef.current = true
        updateUserData('stats', newStats)
        notify('Okunan sayfa istatistiklere eklendi.')
      }
    }

    const interval = setInterval(() => {
      const elapsedTime = Date.now() - pageViewTimeRef.current
      const hasMetMinimumReadTime = elapsedTime >= minimumStatsEntryTimeMs
      const hasMetEstimatedReadTime = elapsedTime >= estimatedReadTimeMs

      if (
        !hasMetMinimumReadTime ||
        (!hasReachedReadingPageEnd() && !hasMetEstimatedReadTime)
      ) {
        return
      }

      addCurrentPageToStats()

      if (statsRecordedRef.current) {
        clearInterval(interval)
      }
    }, 1000)

    const handleScroll = () => {
      const elapsedTime = Date.now() - pageViewTimeRef.current

      if (elapsedTime >= minimumStatsEntryTimeMs && hasReachedReadingPageEnd()) {
        addCurrentPageToStats()
      }

      if (statsRecordedRef.current) {
        clearInterval(interval)
        window.removeEventListener('scroll', handleScroll)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    const historyTimeout = setTimeout(() => {
      const { userData, updateUserData } = latestDataRef.current

      setUserHistory((prevHistory) => {
        if (
          prevHistory[prevHistory.length - 1]?.type === type &&
          prevHistory[prevHistory.length - 1]?.page === page
        ) {
          return prevHistory
        }
        const newHistory = [...prevHistory, { type, page, time: Date.now() }]
        if (newHistory.length >= 50) {
          newHistory.shift()
        }

        localStorage.setItem('userHistory', JSON.stringify(newHistory))
        return newHistory
      })

      if (page > 0) {
        const currentLastRead = userData.lastReadPages?.[type]
        if (currentLastRead !== page) {
          updateUserData('lastReadPages', {
            ...(userData.lastReadPages || {}),
            [type]: page,
          })
        }
      }
    }, 1000)

    return () => {
      clearInterval(interval)
      window.removeEventListener('scroll', handleScroll)
      clearTimeout(historyTimeout)
    }
  }, [page, type, loading])

  useEffect(() => {
    const userHistory = JSON.parse(localStorage.getItem('userHistory') || '[]')
    setUserHistory(userHistory)
  }, [])

  return { userData, updateUserData, loading }
}

export default useReadingProgress
